import React from "react";
import InfiniteTable, { InfiniteTableProps } from "../Display/InfiniteTable";
import { API } from "aws-amplify";
import { IConnectListQuery } from "../../types";
import { Subtract } from "utility-types";

type ApiInfiniteTableProps<T> = InfiniteTableProps<T> & {
  dataFetcher: (
    page: number | null,
    pageSize?: number
  ) => Promise<{ data: T[]; count: number }>;
  pageSize: number;
};

type InjectedProps<T> = {
  loadMore: () => Promise<void>;
  hasMoreData: boolean;
  loading: boolean;
  data: T[];
};

type State<T> = {
  page: string | null;
  hasMoreData: boolean;
  total?: number;
  data: T[];
  loading: boolean;
};

class ApiInfiniteTable<RecordType> extends React.Component<
  Subtract<ApiInfiniteTableProps<RecordType>, InjectedProps<RecordType>>,
  State<RecordType>
> {
  static defaultProps = {
    pageSize: 20,
  };
  state = {
    page: null,
    hasMoreData: true,
    loading: false,
    data: [],
    total: 0,
  };

  tableDataSeed: string;

  changeDataSeed = (): void => {
    this.tableDataSeed = `${this.props.tableId}-dt-${Math.floor(Math.random() * 10000)}`;
  };

  componentDidMount(): void {
    if (this.props.tableId) {
      // Add listener for table refresh
      window.addEventListener(`refresh#${this.props.tableId}`, async () => {
        this.changeDataSeed();
        this.setState(
          {
            page: null,
            hasMoreData: true,
            data: [],
          },
          () => {
            this.loadMore();
          }
        );
      });
    }
  }
  loadMore = async (): Promise<void> => {
    this.setState({ loading: true });
    const page = (this.state.page || 0) + 1;
    if (!this.tableDataSeed) {
      this.changeDataSeed();
    }
    const seed = this.tableDataSeed;
    try {
      const response = await this.props.dataFetcher(page, this.props.pageSize);
      if (seed !== this.tableDataSeed) {
        // Seed was changed while loading, we should ignore the data
        this.setState({ loading: false });
        return;
      }
      if (response.data) {
        this.setState((state: State<RecordType>) => ({
          data: [...state.data, ...response.data],
          page,
          total: response.count,
          hasMoreData: (response.data || []).length < this.props.pageSize,
          loading: false,
        }));
        return;
      }
    } catch (e) {}
    this.setState({ loading: false });
  };
  render(): React.ReactElement {
    return (
      <InfiniteTable
        {...this.props}
        loadMore={this.loadMore}
        hasMoreData={this.state.data.length < this.state.total}
        loading={this.state.loading}
        data={this.state.data}
      />
    );
  }
}

export default ApiInfiniteTable;
